import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useRouteInterpolation } from '../../util/routeUtil';
import ElementIcon, { ElementIconElement } from '../ElementIcon';
import { JnxExpr } from '../../util/jnx';
import { Button } from 'src/components/ui';
import Badge, { BadgeElement } from './Badge';
import kebabCase from 'lodash/kebabCase';
import { getTestId } from '../../hooks/useTestId';
import { useWorkpageTestIdScope } from '../../context/WorkpageTestIdContext';

const layouts = ['clear', 'button', 'button icon'] as const;
type LayoutsEnumType = typeof layouts[number];
const theme_colors = ['primary', 'secondary', 'info', 'warning', 'success', 'danger'] as const;
type ThemeColorsEnumType = typeof theme_colors[number]

export type WorkTrayIconElement = ElementIconElement & {
  name?: string,
  label: string,
  color: ThemeColorsEnumType,
  layout?: LayoutsEnumType,
  badge?: BadgeElement,
  route?: string,
  routeExpr?: JnxExpr
};


function WorkTrayIcon({ element, scope }: {
  element: WorkTrayIconElement
  scope: any
}) {
  const { label, color, layout, badge } = element;
  const workpageScope = useWorkpageTestIdScope();
  const iconBase = [workpageScope, element.name || kebabCase(label) || 'icon'].filter(Boolean).join('.');

  const route = useRouteInterpolation(element.route, element.routeExpr, scope);

  function buttonWrapper(node: ReactNode): ReactNode {
    if (route) {
      return <Link className={`btn btn-${color}`} to={route} {...getTestId(iconBase)}>{node as any}</Link>
    }
    return <Button color={color} {...getTestId(iconBase)}>{node}</Button>
  }

  function labelMaker(LabelTag: 'div' | 'span', newlines: boolean) {
    return (
      label ? (
        <LabelTag className="col-lg-6 align-self-sm-center label">
          {newlines ? <NewlineText text={label} /> : label}
        </LabelTag>
      ) : null
    )
  }

  const badgeNode = badge ? <Badge element={badge} scope={scope} /> : null

  const iconNode = <ElementIcon element={element} />;

  return (
    <div className='workTrayIcon'>
      {(layout === 'button icon') ? (
        <>{buttonWrapper(<>{iconNode}{badgeNode}</>)}{labelMaker("div", true)}</>
      ) : (layout === 'button') ? (
        buttonWrapper(<>{iconNode}{labelMaker("span", false)}{badgeNode}</>)
      ) : (
        <>{iconNode}{labelMaker("div", true)}{badgeNode}</>
      )}
    </div>
  );
}

WorkTrayIcon.rootSchema = {
  'properties': {
    'image': {
      ':classNames': 'from-col-1-size-1',
      'title': 'Button Image',
      'ui:field': 'SelectImageField',
      'type': 'object',
      'iconSizing': true,
      'map:field': '',
      'properties': {}
    },
    'fontSize': { 
      'title': 'Icon Size',
      'type': 'string',
      'enum': ['1em', '2em', '4em', '6em', '8em', '10em'],
      'map:field': {'default': 'icons.size', 'path': 'fontSize'}
    },
    'color': {
      'title': 'Color',
      'type': 'string',
      'enum': theme_colors,
      'map:field': 'color'
    },
    'label': { 
      ':classNames': 'from-col-1-size-2',
      'title': 'Label',
      'type': 'string',
      'map:field': 'label'
    },
    'layout': { 
      'title': 'Layout',
      'type': 'string',
      'map:field': 'layout',
      'enum': layouts
    },
    'badge': {
      'type': 'object',
      ':classNames': 'from-col-1-size-3 grid-2-columns',
      'properties': {
        'lookup': {
          'type': 'object',
          ':classNames': 'from-col-1-size-3',
          'ui:field': 'LookupConfigurationField',
          'map:field': 'badge.lookup',
          'forCount': true,
          "properties": {},
        }
      },
    },
    'route': { 
      ':classNames': 'from-col-1-size-2',
      'title': 'Route',
      'type': 'string',
      'map:field': 'route'
    },
  }
};


function NewlineText(props: {text: string}) {
  return props.text.split('\n').map((str, idx) => <p key={idx}>{str}</p>);
}

export default WorkTrayIcon;
